<template>
  <div>
    <v-row>
      <v-col>
        <v-data-table fixed-header
                      disable-sort
                      :headers="tableFields"
                      :items="importedFiles"
                      :items-per-page="-1"
                      mobile-breakpoint="900"
                      :row-props="rowProps"
                      class="archive-files-table"
        >
          <template v-slot:item.name="data">
            <ArchivesAddStep2TableCellName :item="data.item"/>
          </template>
          <template v-slot:item.ref="data">
            <ArchivesAddStep2TableCellRef :item="data.item"/>
          </template>
          <template v-slot:item.signers="data">
            <ArchivesAddStep2TableCellSigners :item="data.item"/>
          </template>
          <template v-slot:item.date="data">
            <ArchivesAddStep2TableCellDate :item="data.item"
                                           @newDate="onNewDate(data.item, $event)"/>
          </template>
          <template v-for="field in additionalFields"
                    :key="field.value"
                    v-slot:[`item.${field.value}`]="data"
          >
            <ArchivesAddStep2TableCellAdditionalField :item="data.item"
                                                      :field="field.value"
            />
          </template>
          <template #bottom />
        </v-data-table>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { ISOToYYYYMMDD } from '@/common/utils/dates'
import { useArchivesStore } from '@/stores/archivesStore'


import ArchivesAddStep2TableCellAdditionalField from './ArchivesAddStep2TableCellAdditionalField.vue'
import ArchivesAddStep2TableCellDate from './ArchivesAddStep2TableCellDate.vue'
import ArchivesAddStep2TableCellName from './ArchivesAddStep2TableCellName.vue'
import ArchivesAddStep2TableCellRef from './ArchivesAddStep2TableCellRef.vue'
import ArchivesAddStep2TableCellSigners from './ArchivesAddStep2TableCellSigners.vue'

const FILE_TYPE_COLORS = {
  ZIP: '#F2B21D',
  PDF: '#D10027',
  WORD: '#0079C7',
  EXCEL: '#068925',
  POWERPOINT: '#CC4D00',
  IMAGE: '#078283',
  FILE: '#5E7997',
}

export default {
  name: 'ArchivesAddStep2',
  setup() {
    const archivesStore = useArchivesStore()
    return { archivesStore }
  },
  components: {
    ArchivesAddStep2TableCellAdditionalField,
    ArchivesAddStep2TableCellDate,
    ArchivesAddStep2TableCellName,
    ArchivesAddStep2TableCellRef,
    ArchivesAddStep2TableCellSigners,
  },
  props: {
    importedFiles: {
      required: true,
      validator: (value) => {
        return value === undefined || Array.isArray(value)
      },
    },
  },
  data () {
    return {
      tableFields: [],
      additionalFields: [],
    }
  },
  computed: {
    metadata() {
      return this.archivesStore.metadata
    },
  },
  created () {
    let additionalFields = []
    if (this.metadata) {
      additionalFields = this.metadata.map((metadata) => ({
        value: metadata.field,
        title: metadata[this.$i18n.locale],
      }))
    }

    this.tableFields = [
      {
        value: 'name',
        title: this.$t('archives.steps.ArchivesAddStep2.name'),
        sortable: false,
      },
      {
        value: 'ref',
        title: this.$t('archives.steps.ArchivesAddStep2.ref'),
      },
      {
        value: 'signers',
        title: this.$t('archives.steps.ArchivesAddStep2.signers'),
      },
      {
        value: 'date',
        title: this.$t('archives.steps.ArchivesAddStep2.date'),
      },
      ...additionalFields,
    ]
    this.additionalFields = additionalFields
  },
  methods: {
    rowProps ({ item }) {
      const style = { '--file-color': '--color-file' }
      const wordExtensions = ['doc', 'docx', 'docm', 'dotx', 'dotm']
      const excelExtensions = ['xls', 'xlt', 'xlm', 'xlsx', 'xlsm', 'xltx', 'xltm']
      const powerpointExtensions = ['ppt', 'pptx', 'pps', 'vnd.ms-powerpoint']
      const imageExtensions = ['jpg', 'jpeg', 'png', 'gif']
      const zipExtensions = ['zip', 'rar', '7z', 'tar', 'gz', 'bz']
      const pdfExtensions = ['pdf']

      if (wordExtensions.includes(item.extension)) {
        style['--file-color'] = FILE_TYPE_COLORS.WORD
      } else if (excelExtensions.includes(item.extension)) {
        style['--file-color'] = FILE_TYPE_COLORS.EXCEL
      } else if (powerpointExtensions.includes(item.extension)) {
        style['--file-color'] = FILE_TYPE_COLORS.POWERPOINT
      } else if (imageExtensions.includes(item.extension)) {
        style['--file-color'] = FILE_TYPE_COLORS.IMAGE
      } else if (zipExtensions.includes(item.extension)) {
        style['--file-color'] = FILE_TYPE_COLORS.ZIP
      } else if (pdfExtensions.includes(item.extension)) {
        style['--file-color'] = FILE_TYPE_COLORS.PDF
      } else {
        style['--file-color'] = FILE_TYPE_COLORS.FILE
      }

      return { style }
    },
    onNewDate (item, date) {
      item.objectToPost.fileSigningDate = ISOToYYYYMMDD(date)
    },
  },
}
</script>

<style scoped lang="scss">
@import "@/scss/border-tokens";
@import "@/scss/typography-tokens";

:deep(.column-limit td) {
  max-width: 250px;
}

.archive-files-table {
  .v-data-table-header-mobile {
    display: none;
  }

  :deep(table) {
    border-spacing: 0 0.5rem !important;
    padding-bottom: 1rem;

    thead {
      color: rgb(var(--v-theme-text-primary));

      // Applied here and not in the template: the header cells are rendered by
      // `v-data-table` itself, and its own `$table-font-size` / `line-height`
      // would otherwise win over the token.
      th {
        .v-data-table-header__content {
          @include text('heading-xs-bold');
        }
      }
    }

    tbody {
      tr {
        // Deliberate exception: this value is reused below as both the corner radius
        // (border-radius-3, 8px, tokenized) and the left accent bar's border-left width, so it
        // can't be swapped for a border-width token (only 0/1/2px exist) without breaking the
        // rounded-corner visual match (AB#3108019).
        --border-radius: #{get-radius('border-radius-3')};
        background: rgb(var(--v-theme-surface-secondary));

        // Same reason as the header: `v-data-table` renders the cells and sets
        // its own font-size / line-height on them.
        td {
          @include text('body-md-regular');
          color: rgb(var(--v-theme-text-secondary));
        }

        td:not(.v-data-table__mobile-row) {
          width: 20%;

          &:first-child {
            width: 40%;
          }
        }

        td:first-child {
          border-left: var(--border-radius) solid var(--file-color);
          border-top-left-radius: var(--border-radius);
          border-bottom-left-radius: var(--border-radius);
        }
        td:last-child {
          border-top-right-radius: var(--border-radius);
          border-bottom-right-radius: var(--border-radius);
        }

        &:hover {
          background:
            linear-gradient(rgba(var(--v-theme-surface-hover)), rgba(var(--v-theme-surface-hover)))
            rgb(var(--v-theme-surface-secondary));
        }
      }
    }
  }

  :deep(.column-limit td) {
    max-width: 250px;
  }
}
</style>
